import CitySEOHead from "@/components/feature/CitySEOHead";
import SaltLakeCityHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";

const slcProcessingOptions = [
  {
    speed: "24",
    unit: "Hours*",
    label: "SLC Passport Agency",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Appointment required — no walk-ins accepted",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis",
      "Expect to wait all day or return next day",
    ],
    highlight: false,
    emergency: true,
  },
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Predictable turnaround — no guessing",
      "Ideal for LDS missions, study abroad, business",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service",
      "Good for planned travel — family vacations, missions with advance notice",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "Workload & wait times shift with shutdowns and budget fights",
      "In-person clerk appointment required",
      "No document review — errors cause rejection",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Can't upgrade speed after submission",
      "Not suitable if travel is within 10 weeks",
    ],
    highlight: false,
    muted: true,
  },
];

export const faqs = [
  {
    q: "Where is the nearest passport agency to Salt Lake City?",
    a: "The nearest regional passport agency is the Salt Lake City Passport Agency, located at 324 S State St, Suite 225, Salt Lake City, UT 84111. Walk-in appointments are difficult to get. Our courier service bypasses the agency line entirely — we hand-carry your application directly.",
  },
  {
    q: "How fast can I get a passport in Salt Lake City?",
    a: "Through our expedited courier service, passports can be issued as fast as 24 hours for true emergencies. Standard expedited service takes 5–7 business days. Processing time depends on your travel date and the service level you select at checkout.",
  },
  {
    q: "Do I need to visit an office for your Salt Lake City passport service?",
    a: "No office visit needed. You mail us your documents (or use our overnight label), and we courier them directly to the U.S. State Department in Washington, D.C. Your completed passport is FedEx'd back to your door.",
  },
  {
    q: "I have a BYU study abroad program starting in 2 weeks — can you help?",
    a: "Absolutely. Two weeks is enough time for our standard expedited service in most cases. For extra peace of mind, our rush and emergency tiers are available. We've helped hundreds of LDS mission and BYU study abroad students get their passports in time.",
  },
  {
    q: "Can you help with passports for a whole family in Salt Lake City?",
    a: "Yes. We handle individual and family applications simultaneously. Each family member needs their own application, but we process them together to minimize total turnaround time.",
  },
  {
    q: "My LDS mission departure was moved up — what do I do?",
    a: "Call us immediately at 877-253-0084. We specialize in short-notice departures and have handled many LDS missionary reassignments. Depending on your exact departure date, our 24–48 hour emergency service may be available.",
  },
  {
    q: "Is your service more affordable than going to the Salt Lake City passport agency directly?",
    a: "For most travelers, yes. The agency charges standard government fees plus requires you to attend in person (losing a day of work). Our courier fee is transparent, and we typically run 30–100% lower than FedEx and other resellers. Plus we handle all the paperwork for you.",
  },
  {
    q: "What documents do I need for a passport renewal in Salt Lake City?",
    a: "For adult renewals: your most recent passport, a completed DS-82 form, a new passport photo, and payment. We provide a complete checklist at checkout and review your documents before submission to catch any errors.",
  },
];

export default function SaltLakeCityPage() {
  return (
    <>
      <CitySEOHead
        city="Salt Lake City"
        state="Utah"
        stateAbbr="UT"
        route="/cities/salt-lake-city"
        title="Emergency Passport Salt Lake City, UT — Expedited & Fast Passport Service | Fast Passport Center"
        description="Emergency and expedited passport services in Salt Lake City, UT — as fast as 24 hours. Serving LDS mission families, BYU students, SLC business travelers, and state government employees. Registered State Dept. courier. A+ BBB rated. No office visit required."
        geoPlacename="Salt Lake City, Utah"
        geoPosition="40.7608;-111.8910"
        faqs={faqs}
        servicesSchema={[]}
        keywords="emergency passport Salt Lake City, expedited passport Salt Lake City, same day passport Utah, passport renewal Salt Lake City, LDS mission passport"
        statePageName="Utah"
        statePageRoute="/states/utah"
      />
      <div className="font-sans">
        <SaltLakeCityHeroSection />

        {/* Sticky Nav */}
        <div id="sticky-nav" className="bg-[#1A365D] text-white sticky top-0 z-40 hidden lg:block">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex items-center gap-8 h-12 overflow-x-auto">
              {[
                { label: "Processing", href: "#processing" },
                { label: "Services", href: "#services" },
                { label: "Local Area", href: "#local-area" },
                { label: "Travel Timing", href: "#travel-timing" },
                { label: "Testimonials", href: "#testimonials" },
                { label: "Facilities", href: "#facilities" },
                { label: "Compare", href: "#compare" },
                { label: "How It Works", href: "#how-it-works" },
                { label: "FAQ", href: "#faq" },
              ].map((item) => (
                <a key={item.href} href={item.href} className="text-sm text-white/80 hover:text-[#F6C344] whitespace-nowrap transition-colors py-3 border-b-2 border-transparent hover:border-[#F6C344]">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Processing Time */}
        <SharedProcessingTimeSection
          sectionId="processing"
          cityName="Salt Lake City"
          headingExtra="UT"
          introText="The SLC Passport Agency at 324 S State Street is right downtown — but appointments book out weeks in advance and walk-ins are only for verified life-or-death emergencies. Our registered courier service hand-delivers your application daily, so LDS mission families, BYU students, Silicon Slopes executives, and Wasatch Front travelers get their passports on a predictable timeline — no appointment lottery, no waiting room, no second trip downtown."
          options={slcProcessingOptions}
          emergencyDescription="The SLC Passport Agency at 324 S State St serves verified life-or-death emergencies only. You must call NPIC (877-487-2778) and have proof of imminent international travel within 72 hours. Appointments are extremely limited and not guaranteed."
          disclaimerText="* Emergency processing availability depends on U.S. Department of State volume, appointment slots, and your specific circumstances. Travel must be within 72 hours for agency emergency appointments. Courier processing times are based on business days and are not guaranteed — but our daily submission schedule gives Salt Lake City residents the fastest legally available route."
          afterCardsContent={
            <div className="mt-16">
              <div className="text-center mb-10">
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-4">Where Salt Lake City Travelers Are Going</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[
                  { city: "Sao Paulo, Brazil", flag: "🇧🇷", note: "LDS mission hub" },
                  { city: "Tokyo, Japan", flag: "🇯🇵", note: "Tech & study abroad" },
                  { city: "London, UK", flag: "🇬🇧", note: "Business & education" },
                  { city: "Mexico City, Mexico", flag: "🇲🇽", note: "Mission & family" },
                  { city: "Seoul, South Korea", flag: "🇰🇷", note: "Tech partnerships" },
                  { city: "Rome, Italy", flag: "🇮🇹", note: "Study abroad & vacation" },
                ].map((dest) => (
                  <div key={dest.city} className="flex items-center gap-3 bg-[#F7F8FA] rounded-lg p-4">
                    <span className="text-2xl">{dest.flag}</span>
                    <div>
                      <p className="font-bold text-[#1A365D] text-sm">{dest.city}</p>
                      <p className="text-gray-500 text-xs">{dest.note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { stat: "300+", label: "LDS mission countries" },
                  { stat: "24 hrs", label: "Fastest turnaround" },
                  { stat: "20+ yrs", label: "Serving Utah families" },
                  { stat: "4.9★", label: "Customer rating" },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-[#EEF2F8] rounded-lg p-5">
                    <p className="font-bold text-[#1A365D] text-2xl mb-1">{s.stat}</p>
                    <p className="text-gray-600 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        {/* Services */}
        <section id="services" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Handle</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Salt Lake City Residents</h2>
              <p className="text-gray-500 max-w-2xl mx-auto">From first-time LDS mission applicants to Capitol Hill executives needing a second passport, we cover every scenario.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: "ri-passport-line", title: "Passport Renewal", desc: "For adults with a passport issued at age 16+. Mail-in renewal only — no need to appear at a post office. Fastest available route to a new 10-year passport." },
                { icon: "ri-user-add-line", title: "First-Time Passport", desc: "New to international travel? We guide you through DS-11 form requirements and what to bring to your local acceptance facility before we take over." },
                { icon: "ri-parent-line", title: "Child Passport (Under 16)", desc: "Both parents must consent. We walk you through DS-11 minor requirements, parental consent docs, and acceptance clerk appointment booking in Salt Lake County." },
                { icon: "ri-alert-line", title: "Lost or Stolen Passport", desc: "Report your lost passport using DS-64 and apply simultaneously for a replacement. We handle the paperwork and the courier leg for you." },
                { icon: "ri-file-edit-line", title: "Name Change Passport", desc: "Married, divorced, or legally changed your name? We handle the DS-5504 correction process for recently issued passports and full renewals for older ones." },
                { icon: "ri-global-line", title: "Second Passport", desc: "Frequent travelers to countries that won't admit Israeli or other stamps often need two active passports. We handle this specialized request routinely." },
              ].map((svc) => (
                <div key={svc.title} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${svc.icon} text-[#1A365D] text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-lg mb-2">{svc.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Area */}
        <section id="local-area" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Salt Lake City Context</p>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-6">Why Salt Lake City Residents Need Expedited Passport Help</h2>
                <div className="prose prose-gray max-w-none text-gray-600 text-sm leading-relaxed space-y-4">
                  <p>Salt Lake City is unlike most American capitals. As the global headquarters of The Church of Jesus Christ of Latter-day Saints, it generates a consistent, year-round stream of urgent passport requests that no other metropolitan area of its size can match. Each year, tens of thousands of young adults receive mission call letters assigning them to serve for 18–24 months in countries from Brazil to Japan to South Africa. Departure dates are often set only weeks after acceptance — sometimes reassigned with 48 hours of notice when geopolitical events force mission boundaries to shift. For a 19-year-old receiving their first international assignment, a passport that takes 10–12 weeks through standard postal channels simply isn't an option.</p>
                  <p>Beyond the LDS community, Salt Lake City's position as the Wasatch Front's primary economic and governmental hub creates its own passport urgency. The University of Utah and Westminster University together enroll tens of thousands of students, many participating in semester-abroad programs to the UK, South Korea, and across Europe. Technology companies along Silicon Slopes — a corridor stretching from Lehi to Draper — send engineers and executives to international conferences and client meetings with little advance notice. State government officials and federal employees at the IRS regional service center frequently require expedited international travel documentation.</p>
                  <p>Salt Lake City does have a regional passport agency at 324 S State Street, which can be a lifeline for true emergencies — but walk-in appointments are nearly impossible to secure during peak spring and fall travel seasons, and the agency requires proof of departure within 14 days to even consider same-day processing. Our courier service complements the agency by providing a guaranteed, hands-off solution: you mail us your documents, we hand-carry them to the State Department in Washington, D.C., and your completed passport arrives at your doorstep via FedEx overnight. No waiting room. No appointment lottery. No second trip downtown.</p>
                </div>
              </div>
              <div className="space-y-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Utah%20State%20Capitol%20building%20in%20Salt%20Lake%20City%20with%20its%20distinctive%20dome%20against%20the%20Wasatch%20Mountains%20backdrop%2C%20neoclassical%20architecture%20with%20white%20stone%20columns%20and%20grand%20steps%2C%20surrounding%20manicured%20grounds%20with%20trees%20and%20walkways%2C%20clear%20blue%20Utah%20sky%2C%20daytime%20professional%20architectural%20photography%2C%20no%20readable%20text%20or%20signs&width=700&height=450&seq=slc-capitol&orientation=landscape"
                  alt="Utah State Capitol building Salt Lake City"
                  className="w-full h-[250px] object-cover rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Nearest Passport Agency", value: "324 S State St, SLC" },
                    { label: "Airport", value: "SLC International (SLC)" },
                    { label: "Major Universities", value: "U of U, BYU, UVU, WSU" },
                    { label: "Key Industries", value: "Tech (Silicon Slopes), LDS, Govt" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#F7F8FA] rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                      <p className="font-bold text-[#1A365D] text-sm">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Travel Timing */}
        <section id="travel-timing" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Salt Lake City Passport Timing Guide</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { scenario: "LDS Mission Call Received", window: "Apply within 48 hours of call letter", urgency: "High", icon: "ri-map-pin-line", detail: "International mission reassignments can happen up to 1 week before departure. Emergency 24-hour service is available." },
                { scenario: "BYU/U of U Study Abroad", window: "Apply 3–4 weeks before program start", urgency: "Medium", icon: "ri-graduation-cap-line", detail: "Spring and fall semesters see high passport demand at Utah universities. Apply early or use our expedited service." },
                { scenario: "Silicon Slopes Business Travel", window: "Apply as soon as trip is confirmed", urgency: "Variable", icon: "ri-briefcase-line", detail: "Tech executives traveling to London, Seoul, or Toronto need passports with 6+ months validity. We handle renewals fast." },
                { scenario: "Family International Vacation", window: "Apply 6–8 weeks in advance", urgency: "Low-Medium", icon: "ri-plane-line", detail: "Summer Europe trips from SLC often book far in advance. Standard expedited is sufficient, but don't wait past 6 weeks." },
              ].map((item) => (
                <div key={item.scenario} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${item.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{item.scenario}</h3>
                  <p className="text-[#F6C344] text-xs font-bold mb-2 uppercase">{item.window}</p>
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3 ${item.urgency === "High" ? "bg-red-100 text-red-700" : item.urgency === "Medium" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{item.urgency} Urgency</span>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Real Stories</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Salt Lake City Clients</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "Cambria H.", location: "Sugar House, SLC", text: "My son received his mission call to Peru with 12 days to depart. Fast Passport Center had his passport in our hands in 4 days. I can't believe how smooth the whole process was.", stars: 5 },
                { name: "Derek M.", location: "Draper, UT", text: "Silicon Slopes startup, last-minute trip to a London conference. Passport expired the week before. They renewed it in 48 hours. Business saved. Will use again.", stars: 5 },
                { name: "Priya S.", location: "Murray, UT", text: "U of U study abroad in Seoul starting in 3 weeks, my first passport ever. They walked me through the acceptance clerk step, took it from there, and it arrived in 6 days.", stars: 5 },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.stars }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-[#F6C344] text-sm"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A365D] text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Acceptance Facilities */}
        <section id="facilities" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Offices</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Salt Lake City</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-sm">For first-time applicants and minors, you'll need to visit an acceptance facility in person before we take over the courier process.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "Salt Lake City Passport Agency", address: "324 S State St, Suite 225, SLC", hours: "Mon–Fri 8am–3pm (by appt)", type: "Regional Agency" },
                { name: "Salt Lake City Main Post Office", address: "230 W 200 S, Salt Lake City", hours: "Mon–Fri 8am–5pm, Sat 9am–12pm", type: "Acceptance Facility" },
                { name: "Murray Post Office", address: "4951 S State St, Murray", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
                { name: "Draper Post Office", address: "12234 S 900 E, Draper", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
                { name: "Sandy Post Office", address: "560 E 9400 S, Sandy", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
                { name: "West Jordan Post Office", address: "8070 S 1825 W, West Jordan", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
              ].map((fac) => (
                <div key={fac.name} className="border border-gray-200 rounded-lg p-6">
                  <span className="inline-block bg-[#EEF2F8] text-[#1A365D] text-xs font-semibold px-2 py-0.5 rounded-full mb-3">{fac.type}</span>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{fac.name}</h3>
                  <p className="text-gray-600 text-sm mb-1"><i className="ri-map-pin-line mr-1.5"></i>{fac.address}</p>
                  <p className="text-gray-500 text-xs"><i className="ri-time-line mr-1.5"></i>{fac.hours}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section id="compare" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Alternatives</h2>
            </div>

            <div className="bg-amber-50 border border-[#F6C344]/40 rounded-lg p-5 md:p-6 mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-base text-[#1A365D] mb-1">Passport Agency Appointment Not Available?</h3>
                  <p className="text-sm text-gray-600">Don't let a booked-out agency appointment block your travel plans. We bypass the agency bottleneck with reserved daily courier slots — faster and no appointment roulette.</p>
                </div>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A365D] text-white">
                    <th className="text-left px-4 py-3 font-semibold rounded-tl-lg">Feature</th>
                    <th className="text-center px-4 py-3 font-semibold text-[#F6C344]">Fast Passport Center</th>
                    <th className="text-center px-4 py-3 font-semibold">SLC Passport Agency</th>
                    <th className="text-center px-4 py-3 font-semibold rounded-tr-lg">FedEx / UPS</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["No office visit required", "✓", "✗", "✗"],
                    ["As fast as 24 hours", "✓", "Same-day only", "✗"],
                    ["Price vs. resellers", "30–100% less", "Govt fee only", "Full markup"],
                    ["Registered State Dept. courier", "✓", "N/A", "✗"],
                    ["Document error review", "✓", "✗", "✗"],
                    ["A+ BBB rated", "✓", "N/A", "Varies"],
                    ["Works for LDS mission calls", "✓", "Limited", "✗"],
                    ["Handles family applications together", "✓", "✗", "✗"],
                    ["FedEx delivery to your door", "✓", "Pick up in person", "Partial"],
                  ].map(([feat, us, agency, fedex], i) => (
                    <tr key={feat} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                      <td className="px-4 py-3 text-gray-700 font-medium">{feat}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">{us}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{agency}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{fedex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Mistakes */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Salt Lake City Passport Mistakes</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Waiting for a post office appointment", desc: "SLC-area post offices book out 2–3 weeks for acceptance appointments. Start immediately." },
                { title: "Assuming the SLC agency has walk-ins", desc: "The regional agency only accepts walk-ins for life/death emergencies. Book our courier instead." },
                { title: "Missing the 6-month validity rule", desc: "Many countries require 6 months of passport validity beyond your travel dates. Renew early." },
                { title: "Wrong photo size or background", desc: "We review your photos before submission to catch issues that cause rejection." },
                { title: "Forgetting minor consent docs", desc: "Children under 16 require both parents to appear or provide notarized consent. We tell you exactly what's needed." },
                { title: "Not accounting for visa requirements", desc: "Some destinations require visas in addition to a passport. We flag this at checkout." },
              ].map((m) => (
                <div key={m.title} className="flex gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-red-600 text-base"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-sm mb-1">{m.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">The Process</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Salt Lake City Residents</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Order Online", desc: "Select your service level and complete the secure order form. Takes about 5 minutes." },
                { step: "2", title: "Mail Your Documents", desc: "Use our prepaid FedEx label to send your application, photos, and passport to our processing center." },
                { step: "3", title: "We Courier to State Dept.", desc: "Our registered courier hand-delivers your application to the U.S. Department of State in Washington, D.C." },
                { step: "4", title: "Passport Delivered", desc: "Your completed passport ships back to your Salt Lake City address via FedEx Priority Overnight." },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-xl mx-auto mb-4">{s.step}</div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Salt Lake City Passport FAQs</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="border border-gray-200 rounded-lg group">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <h4 className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</h4>
                    <i className="ri-add-line text-[#1A365D] flex-shrink-0 group-open:hidden"></i>
                    <i className="ri-subtract-line text-[#1A365D] flex-shrink-0 hidden group-open:block"></i>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Helpful Resources */}
        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Official Links</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Salt Lake City Passport Applicants</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "U.S. State Dept. Passport Info", url: "https://travel.state.gov", icon: "ri-government-line" },
                { title: "SLC Passport Agency (324 S State St)", url: "https://travel.state.gov/content/travel/en/passports/get-fast/passport-agencies.html", icon: "ri-building-line" },
                { title: "Appointment Scheduler (passportappointment.org)", url: "https://passportappointment.org", icon: "ri-calendar-check-line" },
                { title: "DS-82 Renewal Form (State Dept)", url: "https://travel.state.gov/content/travel/en/passports/need-passport/ds-82.html", icon: "ri-file-text-line" },
                { title: "SLC International Airport", url: "https://www.slcairport.com", icon: "ri-flight-takeoff-line" },
                { title: "Utah DMV (ID Documentation)", url: "https://dld.utah.gov", icon: "ri-id-card-line" },
              ].map((r) => (
                <a key={r.title} href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-4 bg-white rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-colors group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                    <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <span className="text-[#1A365D] font-medium text-sm group-hover:text-[#F6C344] transition-colors">{r.title}</span>
                  <i className="ri-external-link-line text-gray-400 ml-auto"></i>
                </a>
              ))}
            </div>

            {/* Vital Records */}
            <div className="mt-8">
              <div className="text-center mb-8">
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Vital Records</p>
                <h3 className="font-serif text-2xl font-bold text-[#1A365D] mb-2">Utah Vital Records for Passport Applications</h3>
                <p className="text-gray-500 max-w-2xl mx-auto text-sm">If you need certified copies of birth, death, marriage, or divorce records for your passport application, these Utah agencies can help:</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                {[
                  { label: "Birth Certificate", agency: "Utah Department of Health — Vital Records", address: "288 N 1460 W, Salt Lake City, UT 84116", url: "https://vitalrecords.utah.gov/" },
                  { label: "Death Certificate", agency: "Utah Department of Health — Vital Records", address: "288 N 1460 W, Salt Lake City, UT 84116", url: "https://vitalrecords.utah.gov/" },
                  { label: "Marriage Certificate", agency: "Salt Lake County Clerk", address: "2001 S State St, Salt Lake City, UT 84190", url: "https://slco.org/clerk/" },
                  { label: "Divorce Decree", agency: "Utah State Courts — District Court", address: "450 S State St, Salt Lake City, UT 84111", url: "https://www.utcourts.gov/" },
                ].map((record) => (
                  <a key={record.label} href={record.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-start gap-3 bg-white rounded-lg p-4 border border-gray-200 hover:border-[#F6C344] transition-colors group">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className="ri-file-text-line text-[#1A365D] text-base"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#1A365D] text-sm group-hover:text-[#F6C344] transition-colors">{record.label}</p>
                      <p className="text-gray-500 text-xs mt-0.5">{record.agency}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{record.address}</p>
                    </div>
                    <i className="ri-external-link-line text-gray-400 ml-auto flex-shrink-0"></i>
                  </a>
                ))}
              </div>
              <div className="mt-6 max-w-3xl mx-auto">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-center">
                  <p className="text-gray-600 text-xs">
                    <i className="ri-information-line text-amber-500 mr-1.5"></i>
                    For out-of-state records, you can also use <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer nofollow" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek</a> to order certified copies from any U.S. state.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <SEOCrossLinks currentRoute="/cities/salt-lake-city" state="UT" />

      {/* Final CTA */}
      <section className="bg-[#1A365D] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">Get Started Today</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">Salt Lake City's Fastest Passport Service</h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">Whether you're sending a missionary, boarding a Delta flight from SLC, or heading to a conference in Tokyo — we get your passport done. As fast as 24 hours.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              Start My Passport <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:8772530084" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i>877-253-0084
            </a>
          </div>
        </div>
      </section>
    </>
  );
}